import { useEffect, useState } from "react";
import { Box, Typography, Stack, Button, Paper } from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addMinutes, isBefore, format } from "date-fns";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

const TimeSelector = ({ value = null, onChange, disabled = false }) => {
  const [time, setTime] = useState(value);

  useEffect(() => {
    setTime(value ?? null);
  }, [value]);

  const handleChange = (newValue) => {
    if (disabled) return;

    if (!newValue) {
      setTime(null);
      onChange?.(null);
      return;
    }

    const nowPlusOne = addMinutes(new Date(), 1);
    const safeValue = isBefore(newValue, nowPlusOne) ? nowPlusOne : newValue;

    setTime(safeValue);
    onChange?.(safeValue);
  };

  const clearSchedule = () => {
    if (disabled) return;
    setTime(null);
    onChange?.(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <ScheduleOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Release time
          </Typography>
        </Stack>

        {!disabled ? (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              p: 1.5,
              mb: 2,
              bgcolor: "#fafafa",
              border: "1px solid #eee",
              borderRadius: "10px",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">
              Leave empty to send immediately, or pick a future date and time to schedule.
            </Typography>
          </Paper>
        ) : null}

        <DateTimePicker
          label="Release date & time"
          value={time}
          disabled={disabled}
          minDateTime={disabled ? undefined : addMinutes(new Date(), 1)}
          onChange={handleChange}
          slotProps={{
            textField: {
              fullWidth: true,
              placeholder: "Send immediately",
              size: "small",
            },
            field: { clearable: !disabled },
          }}
        />

        {!disabled && time ? (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Will send at {format(time, "dd MMM yyyy, HH:mm")}
            </Typography>
            <Button
              size="small"
              onClick={clearSchedule}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Send immediately instead
            </Button>
          </Stack>
        ) : null}

        {!disabled && !time ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Will send immediately on submit
          </Typography>
        ) : null}

        {disabled && time ? (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Scheduled for {format(time, "dd MMM yyyy, HH:mm")}
          </Typography>
        ) : null}

        {disabled && !time ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Sent immediately
          </Typography>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
};

export default TimeSelector;
